import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { RouterProvider } from "react-router-dom";
import { toast } from "sonner";

import { AppErrorBoundary } from "@/components/app/error-state";
import { Toaster } from "@/components/ui/sonner";
import { fetchCsrfToken, registerApiHandlers } from "@/lib/api";
import { i18n } from "@/lib/i18n";
import { qk } from "@/lib/query-keys";
import { queryClient } from "@/lib/query-client";
import { router } from "@/router";
import { useAppStore } from "@/stores/app-store";
import { useTheme } from "@/hooks/use-theme";

/**
 * Providers, in the order everything below them expects:
 * error boundary → i18n → query client → router, with the toaster alongside.
 */
export function App() {
  const setTenantSuspended = useAppStore((s) => s.setTenantSuspended);
  const { theme } = useTheme();

  useEffect(() => {
    // One CSRF fetch at startup, before any mutation can run (contract §7.4).
    void fetchCsrfToken();

    registerApiHandlers({
      onUnauthenticated: () => {
        queryClient.setQueryData(qk.session(), undefined);
        void queryClient.invalidateQueries({ queryKey: qk.session() });
        if (!window.location.pathname.startsWith("/login")) {
          router.navigate("/login", { state: { from: window.location.pathname } });
        }
      },
      onTenantSuspended: (reason) => setTenantSuspended(true, reason),
      onNotify: (error) => {
        // 403 and 5xx are worth a toast; everything else is the caller's business.
        toast.error(error.detail);
      },
    });
  }, [setTenantSuspended]);

  return (
    <AppErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Toaster theme={theme} position="top-right" richColors closeButton />
        </QueryClientProvider>
      </I18nextProvider>
    </AppErrorBoundary>
  );
}
